<template>
  <v-card>
    <v-card-title>
      <v-row no-gutters>
        <v-col>
          <h5>Vertragsnummer {{ contract.id }} <span class="text-uppercase">({{ scopeLabel }})</span></h5>
          <div
            v-if="contract.valid_from || contract.valid_notafter"
            class="text-subtitle-2 text--secondary"
          >
            {{ momentize(contract.valid_from) }} - {{ momentize(contract.valid_notafter) || 'unbegrenzt' }}
            <span v-if="isReadonly">(abgelaufen)</span>
          </div>
        </v-col>
      </v-row>
    </v-card-title>
    <v-card-text v-if="contract">
      <v-form>
        <v-row>
          <v-col cols="12">
            <v-autocomplete
              :items="filteredBillingPlans"
              item-value="url"
              item-text="name"
              v-model="selectedBillingPlanUrl"
              placeholder="Rechnungskonfiguration wählen"
              :disabled="!isSuperuser || isReadonly"
              :append-outer-icon="selectedBillingPlanUrl ? mdiHelpCircleOutline : null"
              @click:append-outer="showBillingPlanDetails = true"
            />
          </v-col>
        </v-row>
        <v-row v-if="isNewAssociation">
          <v-col
            cols="12"
            md="6"
          >
            <text-field-date-picker
              v-model="newAssociationValidFrom"
              label="Gültig von"
              :disabled="!isSuperuser || isReadonly"
              hide-hint
            />
          </v-col>
          <v-col
            cols="12"
            md="6"
          >
            <text-field-date-picker
              v-model="newAssociationValidNotafter"
              label="Gültig bis"
              :disabled="!isSuperuser || isReadonly"
              hide-hint
            />
          </v-col>
        </v-row>
      </v-form>
      <v-card
        v-for="association in editableAssociations"
        :key="association.id"
        class="mb-4"
        outlined
      >
        <v-card-title>
          {{ getBillingPlanName(association.billing_plan) }}
          <v-spacer />
          <v-btn
            icon
            @click="selectedBillingPlanUrl = association.billing_plan; showBillingPlanDetails = true"
          >
            <v-icon>{{ mdiHelpCircleOutline }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col
              cols="12"
              md="6"
            >
              <text-field-date-picker
                v-model="association.valid_from"
                label="Gültig von"
                :disabled="!isSuperuser || isReadonly || isPastAssociation(association)"
                hide-hint
              />
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <text-field-date-picker
                v-model="association.valid_notafter"
                label="bis"
                :disabled="!isSuperuser || isReadonly || isPastAssociation(association)"
                hide-hint
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
      <v-form>
        <v-dialog
          v-model="showBillingPlanDetails"
          max-width="600px"
        >
          <v-card v-if="selectedBillingPlan">
            <v-card-title>
              Details für {{ selectedBillingPlan.name }}
            </v-card-title>
            <v-card-text>
              <v-row
                v-for="(priceDefinitions, priceDefinitionScope) in selectedBillingPlan.conditions[scope]"
                :key="priceDefinitionScope"
              >
                <v-col
                  cols="12"
                  md="4"
                >{{ getPriceDefinitionScopeLabel(priceDefinitionScope) }}
                </v-col>
                <v-col
                  cols="12"
                  md="8"
                >
                  <v-row>
                    <template
                      v-for="(priceDefinition, priceDefinitionKey) in priceDefinitions"
                    >
                      <v-col
                        v-if="Array.isArray(priceDefinition)"
                        :key="priceDefinitionKey"
                        cols="12"
                        md="6"
                      >
                        <v-text-field
                          :label="'ab ' + priceDefinition[0]"
                          :value="priceDefinition[1]"
                          class="mx-4"
                          :append-icon="mdiCurrencyEur"
                          disabled
                        />
                      </v-col>
                      <template v-else>
                        <template v-for="transactionPriceDefinitions in priceDefinition">
                          <v-col
                            v-for="(transactionPriceDefinition, transactionPriceDefinitionKey) in transactionPriceDefinitions"
                            :key="transactionPriceDefinitionKey"
                            cols="12"
                            md="6"
                          >
                            <v-text-field
                              :label="'ab ' + transactionPriceDefinition[0]"
                              :value="transactionPriceDefinition[1]"
                              class="mx-4"
                              :append-icon="mdiCurrencyEur"
                              disabled
                            />
                          </v-col>
                        </template>
                      </template>
                    </template>
                  </v-row>
                </v-col>
              </v-row>
              <v-row>
                <v-col
                  cols="12"
                  md="4"
                  class="my-4"
                >
                  Rechnungslegung
                </v-col>
                <v-col
                  cols="12"
                  md="8"
                >
                  <v-radio-group
                    :value="selectedBillingPlan.type"
                    row
                    disabled
                    class="mx-4"
                  >
                    <v-radio
                      label="monatlich"
                      value="monthly"
                    />
                    <v-radio
                      label="jährlich"
                      value="yearly"
                    />
                  </v-radio-group>
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-actions>
              <v-spacer/>
              <v-btn
                text
                color="primary"
                @click="showBillingPlanDetails = false"
              >
                Schließen
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-row>
          <v-col
            cols="12"
            md="3"
            class="my-4"
          >
            SEPA-Lastschrift-mandat erteilt?
          </v-col>
          <v-col
            cols="12"
            md="9"
          >
            <v-radio-group
              row
              class="mx-4"
              :disabled="!isSuperuser || isReadonly"
              v-model="selectedSepaDdActive"
            >
              <v-radio
                label="Ja"
                :value="true"
              />
              <v-radio
                label="Nein"
                :value="false"
              />
            </v-radio-group>
            <v-alert
              v-if="selectedSepaDdActive && !hasBillingSourceAccount"
              type="warning"
              dense
              text
              class="mt-0"
            >
              Für SEPA-Lastschrift muss ein Bankkonto mit Verwendung „Geldabgang Lastschrift" verknüpft sein.
            </v-alert>
          </v-col>
        </v-row>
      </v-form>
      <contract-bank-accounts-section
        :contract="contract"
        :organization="organization"
        @relations-changed="contractAccountRelations = $event"
      />
    </v-card-text>
    <v-card-actions>
      <v-spacer/>
      <v-btn
        text
        color="primary"
        @click="saveContractBilling"
        :disabled="isReadonly || !hasChange || (selectedSepaDdActive && !hasBillingSourceAccount)"
      >
        Speichern
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { CONTRACT_PRODUCT_CLASSES } from "@/lib/device-db"
import { mdiCurrencyEur, mdiHelpCircleOutline } from "@mdi/js"
import { mapGetters } from "vuex"
import {
  CREATE_BILLING_PLAN_CONTRACT_ASSOCIATION,
  UPDATE_BILLING_PLAN_CONTRACT_ASSOCIATION, UPDATE_CONTRACT
} from "@/store/action-types"
import moment from "moment/moment"
import { CONTRACT_SCOPE_ECOMMERCE, CONTRACT_SCOPE_KNB } from "@/store/contracts"
import { getLatestVersion, isCurrentlyValid } from "@/lib/sort-utils"
import TextFieldDatePicker from "@/components/UI/TextFieldDatePicker.vue"
import ContractBankAccountsSection from "@/components/ContractBankAccountsSection.vue"

export default {
  name: 'OrganizationContractBillingCard',
  components: { TextFieldDatePicker, ContractBankAccountsSection },
  emits: ['update'],
  props: {
    organization: {
      type: Object,
      required: true
    },
    contract: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      initialBillingPlanUrl: null,
      selectedBillingPlanUrl: null,
      initialSepaDdActive: null,
      selectedSepaDdActive: null,
      showBillingPlanDetails: false,
      latestAssociation: null,
      editableAssociations: [],
      newAssociationValidFrom: moment().startOf('day').format('YYYY-MM-DD'),
      newAssociationValidNotafter: null,
      contractAccountRelations: [],
      mdiCurrencyEur,
      mdiHelpCircleOutline
    }
  },
  computed: {
    ...mapGetters('billing', ['billingPlans', 'billingPlanByUrl']),
    ...mapGetters('user', ['isSuperuser']),
    isNewAssociation () {
      return this.selectedBillingPlanUrl && this.selectedBillingPlanUrl !== this.initialBillingPlanUrl
    },
    filteredBillingPlans () {
      return this.billingPlans.filter(plan => {
        if (this.contract.type === 'ps') {
          return plan.conditions && plan.conditions[CONTRACT_SCOPE_KNB]
        }
        if (this.contract.type === 'op') {
          return plan.conditions && plan.conditions[CONTRACT_SCOPE_ECOMMERCE]
        }
        return true
      }).sort((a, b) => a.name.localeCompare(b.name))
    },
    selectedBillingPlan () {
      return this.billingPlanByUrl[this.selectedBillingPlanUrl]
    },
    sortedAssociations () {
      if (!this.contract.billingPlanContractAssociations) {
        return []
      }
      return [...this.contract.billingPlanContractAssociations].sort((a, b) => {
        return moment(b.valid_from).diff(moment(a.valid_from))
      })
    },
    hasChange () {
      if (this.selectedSepaDdActive !== this.initialSepaDdActive) {
        return true
      }
      if (this.selectedBillingPlanUrl !== this.initialBillingPlanUrl) {
        return true
      }
      const initialAssociations = this.sortedAssociations
      if (this.editableAssociations.length !== initialAssociations.length) {
        return true
      }
      return this.editableAssociations.some((assoc, index) => {
        const initial = initialAssociations[index]
        return assoc.valid_from !== initial.valid_from ||
          assoc.valid_notafter !== initial.valid_notafter
      })
    },
    hasBillingSourceAccount () {
      return this.contractAccountRelations.some(r => r.usage === 'billing_source')
    },
    scope () {
      if (this.contract.type === 'op') {
        return CONTRACT_SCOPE_ECOMMERCE
      }

      if (this.contract.type === 'ps') {
        return CONTRACT_SCOPE_KNB
      }

      return this.contract.type
    },
    scopeLabel () {
      if (this.contract.type === 'service') {
        return 'Kostenübernahme'
      }

      return this.scope
    },
    isReadonly () {
      if (isCurrentlyValid(this.contract)) {
        return false
      }

      const validFrom = this.contract.valid_from
      if (validFrom && moment().isBefore(moment(validFrom, [moment.ISO_8601, 'DD.MM.YYYY']))) {
        return false
      }

      return true
    }
  },
  methods: {
    momentize (date) {
      if (!date) return null
      return moment(date).format('DD.MM.YYYY')
    },
    isPastAssociation (association) {
      if (!association.valid_notafter) {
        return false
      }
      return moment(association.valid_notafter).isBefore(moment().subtract(6, 'months'), 'day')
    },
    getPriceDefinitionScopeLabel (scope) {
      const labelMapping = {
        ...CONTRACT_PRODUCT_CLASSES,
        per_tx: 'Kosten pro Transaktion',
        per_device: 'Kosten pro Gerät'
      }
      return labelMapping[scope] || scope
    },
    getBillingPlanName (url) {
      return this.billingPlanByUrl[url]?.name || url
    },
    async saveContractBilling () {
      if (this.selectedSepaDdActive && !this.hasBillingSourceAccount) {
        return
      }
      if (this.isNewAssociation) {
        await this.createNewContractBilling()
      } else if (this.latestAssociation) {
        await this.updateContractBilling()
      } else {
        await this.createContractBilling()
      }

      this.$emit('update')
      this.initialBillingPlanUrl = this.selectedBillingPlanUrl
      this.initialSepaDdActive = this.selectedSepaDdActive
    },
    async createNewContractBilling () {
      const actions = []

      // 1. Create the new association
      actions.push(this.$store.dispatch('contracts/' + CREATE_BILLING_PLAN_CONTRACT_ASSOCIATION, {
        contract: this.contract,
        billingPlanUrl: this.selectedBillingPlanUrl,
        validFrom: this.newAssociationValidFrom,
        validNotafter: this.newAssociationValidNotafter
      }))

      // 2. Update the previous latest association if it exists
      if (this.latestAssociation) {
        const previousValidNotafter = moment(this.newAssociationValidFrom).subtract(1, 'day').startOf('day')
        actions.push(this.$store.dispatch('contracts/' + UPDATE_BILLING_PLAN_CONTRACT_ASSOCIATION, {
          associationId: this.latestAssociation.id,
          contractId: this.contract.id,
          billingPlanUrl: this.latestAssociation.billing_plan,
          validFrom: this.latestAssociation.valid_from,
          validNotafter: previousValidNotafter
        }))
      }

      if (this.selectedSepaDdActive !== this.initialSepaDdActive) {
        actions.push(this.updateContractSepaDdActive())
      }

      return await Promise.allSettled(actions)
    },
    async createContractBilling () {
      const billingPlanCreate = this.$store.dispatch('contracts/' + CREATE_BILLING_PLAN_CONTRACT_ASSOCIATION, {
        contract: this.contract,
        billingPlanUrl: this.selectedBillingPlanUrl
      })

      const actions = [billingPlanCreate]

      if (this.selectedSepaDdActive !== this.initialSepaDdActive) {
        actions.push(this.updateContractSepaDdActive())
      }

      return await Promise.allSettled(actions)
    },
    async updateContractBilling () {
      const actions = []

      this.editableAssociations.forEach(assoc => {
        const initial = this.sortedAssociations.find(a => a.id === assoc.id)
        const isLatest = this.latestAssociation && this.latestAssociation.id === assoc.id
        const billingPlanUrl = isLatest ? this.selectedBillingPlanUrl : assoc.billing_plan

        const hasValidityChange = assoc.valid_from !== initial.valid_from ||
          assoc.valid_notafter !== initial.valid_notafter
        const hasPlanChange = isLatest && this.selectedBillingPlanUrl !== this.initialBillingPlanUrl

        if (hasValidityChange || hasPlanChange) {
          actions.push(this.$store.dispatch('contracts/' + UPDATE_BILLING_PLAN_CONTRACT_ASSOCIATION, {
            associationId: assoc.id,
            contractId: this.contract.id,
            billingPlanUrl,
            validFrom: assoc.valid_from,
            validNotafter: assoc.valid_notafter
          }))
        }
      })

      if (this.selectedSepaDdActive !== this.initialSepaDdActive) {
        actions.push(this.updateContractSepaDdActive())
      }

      return await Promise.allSettled(actions)
    },
    parseDate (date) {
      if (!date) return null
      const [day, month, year] = date.split('.').map(Number)
      return moment(`${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`).startOf('day').toISOString()
    },
    updateContractSepaDdActive () {
      return this.$store.dispatch('contracts/' + UPDATE_CONTRACT, {
        contract: this.contract,
        sepaDdActive: this.selectedSepaDdActive
      })
    },
  },
  watch: {
    contract: {
      immediate: true,
      handler (contract) {
        this.latestAssociation = getLatestVersion(contract.billingPlanContractAssociations || [])
        this.initialBillingPlanUrl = this.latestAssociation?.billing_plan
        this.selectedBillingPlanUrl = this.initialBillingPlanUrl

        this.editableAssociations = this.sortedAssociations.map(assoc => ({
          id: assoc.id,
          billing_plan: assoc.billing_plan,
          valid_from: assoc.valid_from,
          valid_notafter: assoc.valid_notafter
        }))

        if (this.initialSepaDdActive === null) {
          this.initialSepaDdActive = contract.sepadd_active
          this.selectedSepaDdActive = contract.sepadd_active
        }
      }
    }
  }
}
</script>
